import React, { useState, useEffect } from 'react';
import { statsApi, companyApi, authApi, userApi } from '../lib/api';
import MoonWidget from '../components/widgets/MoonWidget';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Building2,
  Users,
  CheckCircle,
  TrendingUp,
  Plus,
  ToggleLeft,
  ToggleRight,
  Trash2,
  Loader2,
  Key,
  UserPlus,
  Pencil,
  Palette,
  Save,
  Settings,
  Mail,
  Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const SuperAdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newCompany, setNewCompany] = useState({ name: '', slug: '' });
  const [creating, setCreating] = useState(false);
  
  // Edit company
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  
  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Users management
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showUsersDialog, setShowUsersDialog] = useState(false);
  
  // Reset password
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  
  // Create user
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'admin' });
  const [creatingUser, setCreatingUser] = useState(false);

  // Delete user
  const [showDeleteUserConfirm, setShowDeleteUserConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);

  // Super Admin credentials
  const [showCredentialsDialog, setShowCredentialsDialog] = useState(false);
  const [credentials, setCredentials] = useState({ email: '', currentPassword: '', newPassword: '' });
  const [savingCredentials, setSavingCredentials] = useState(false);

  // Predefined themes
  const themes = [
    { name: 'Azul Padrão', primary: '#0ea5e9', primary_dark: '#0284c7' },
    { name: 'Verde', primary: '#22c55e', primary_dark: '#16a34a' },
    { name: 'Roxo', primary: '#8b5cf6', primary_dark: '#7c3aed' },
    { name: 'Rosa', primary: '#ec4899', primary_dark: '#db2777' },
    { name: 'Laranja', primary: '#f97316', primary_dark: '#ea580c' },
    { name: 'Vermelho', primary: '#ef4444', primary_dark: '#dc2626' },
    { name: 'Amarelo', primary: '#eab308', primary_dark: '#ca8a04' },
    { name: 'Ciano', primary: '#06b6d4', primary_dark: '#0891b2' },
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, companiesRes] = await Promise.all([
        statsApi.getDashboard(),
        companyApi.list(),
      ]);
      setStats(statsRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = async () => {
    if (!newCompany.name.trim()) {
      toast.error('Digite o nome da empresa');
      return;
    }

    setCreating(true);
    try {
      const slug = newCompany.slug.trim() || newCompany.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      await companyApi.create({ 
        name: newCompany.name, 
        slug: slug,
        logo_url: ''
      });
      toast.success('Empresa criada com sucesso!');
      setNewCompany({ name: '', slug: '' });
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar empresa');
    } finally {
      setCreating(false);
    }
  };

  const openEditDialog = (company) => {
    setEditingCompany({
      ...company,
      theme: company.theme || { primary: '#0ea5e9', primary_dark: '#0284c7' }
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editingCompany.name.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }
    
    setSavingEdit(true);
    try {
      await companyApi.update(editingCompany.id, {
        name: editingCompany.name,
        slug: editingCompany.slug,
        logo_url: editingCompany.logo_url || '',
        theme: editingCompany.theme
      });
      toast.success('Empresa atualizada!');
      setShowEditDialog(false);
      setEditingCompany(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleToggleStatus = async (companyId, currentStatus) => {
    try {
      await companyApi.toggleStatus(companyId);
      toast.success(currentStatus ? 'Empresa bloqueada' : 'Empresa ativada');
      loadData();
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  const confirmDelete = (company) => {
    setCompanyToDelete(company);
    setShowDeleteConfirm(true);
  };

  const handleDeleteCompany = async () => {
    if (!companyToDelete) return;
    
    setDeleting(true);
    try {
      await companyApi.delete(companyToDelete.id);
      toast.success('Empresa excluída!');
      setShowDeleteConfirm(false);
      setCompanyToDelete(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir empresa');
    } finally {
      setDeleting(false);
    }
  };

  const handleViewUsers = async (company) => {
    setSelectedCompany(company);
    setShowUsersDialog(true);
    setLoadingUsers(true);
    
    try {
      const res = await companyApi.getUsers(company.id);
      setCompanyUsers(res.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Erro ao carregar usuários');
      setCompanyUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword.trim() || newPassword.length < 6) {
      toast.error('A senha deve ter no mínimo 6 caracteres');
      return;
    }
    
    setResettingPassword(true);
    try {
      await companyApi.resetUserPassword(selectedUser.id, newPassword);
      toast.success(`Senha de ${selectedUser.name || selectedUser.email} resetada!`);
      setShowResetDialog(false);
      setNewPassword('');
      setSelectedUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao resetar senha');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.name.trim() || !newUser.email.trim() || !newUser.password.trim()) {
      toast.error('Preencha todos os campos');
      return;
    }
    
    setCreatingUser(true);
    try {
      await companyApi.createUser(selectedCompany.id, newUser);
      toast.success('Usuário criado com sucesso!');
      setShowCreateUserDialog(false);
      setNewUser({ name: '', email: '', password: '', role: 'admin' });
      handleViewUsers(selectedCompany);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar usuário');
    } finally {
      setCreatingUser(false);
    }
  };

  const handleUpdateCredentials = async () => {
    if (!credentials.currentPassword) {
      toast.error('Digite sua senha atual');
      return;
    }
    
    if (!credentials.email && !credentials.newPassword) {
      toast.error('Digite o novo email ou a nova senha');
      return;
    }
    
    if (credentials.newPassword && credentials.newPassword.length < 6) {
      toast.error('A nova senha deve ter no mínimo 6 caracteres');
      return;
    }
    
    setSavingCredentials(true);
    try {
      await authApi.updateSuperAdminCredentials(
        credentials.currentPassword,
        credentials.email || null,
        credentials.newPassword || null
      );
      toast.success('Credenciais atualizadas com sucesso!');
      setShowCredentialsDialog(false);
      setCredentials({ email: '', currentPassword: '', newPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar credenciais');
    } finally {
      setSavingCredentials(false);
    }
  };

  const openResetDialog = (user) => {
    setSelectedUser(user);
    setNewPassword(selectedCompany?.slug ? `${selectedCompany.slug}123` : 'nova123');
    setShowResetDialog(true);
  };

  const openDeleteUserDialog = (user) => {
    setUserToDelete(user);
    setShowDeleteUserConfirm(true);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    setDeletingUser(true);
    try {
      await userApi.delete(userToDelete.id);
      toast.success('Usuário excluído com sucesso!');
      setShowDeleteUserConfirm(false);
      setUserToDelete(null);
      handleViewUsers(selectedCompany);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir usuário');
    } finally {
      setDeletingUser(false);
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      'admin': 'Admin',
      'diretoria': 'Diretoria',
      'user': 'Usuário',
      'viewer': 'Visualizador',
    };
    return labels[role] || role;
  };

  const getRoleColor = (role) => {
    const colors = {
      'admin': 'bg-blue-100 text-blue-700',
      'diretoria': 'bg-blue-100 text-blue-700',
      'user': 'bg-slate-100 text-slate-700',
      'viewer': 'bg-gray-100 text-gray-700',
    };
    return colors[role] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid="super-admin-dashboard" className="space-y-8 animate-fade-in">
      {/* Header with credentials button */}
      <div className="flex justify-end">
        <Button
          variant="outline"
          onClick={() => setShowCredentialsDialog(true)}
          className="gap-2"
          data-testid="edit-credentials-btn"
        >
          <Settings className="w-4 h-4" />
          Minhas Credenciais
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Total Empresas</CardTitle>
            <Building2 className="w-5 h-5 text-slate-400" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900">{stats?.total_companies || 0}</div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Empresas Ativas</CardTitle>
            <CheckCircle className="w-5 h-5 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">{stats?.active_companies || 0}</div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Total Usuários</CardTitle>
            <Users className="w-5 h-5 text-slate-400" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900">{stats?.total_users || 0}</div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Total Tarefas</CardTitle>
            <TrendingUp className="w-5 h-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">{stats?.total_tasks || 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Moon Widget + Companies */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <MoonWidget />
        </div>
        
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Empresas Cadastradas</CardTitle>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary">
                  <Plus className="w-4 h-4 mr-2" />
                  Nova Empresa
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Criar Nova Empresa</DialogTitle>
                  <DialogDescription>Adicione uma nova empresa ao sistema</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label>Nome da Empresa *</Label>
                    <Input
                      value={newCompany.name}
                      onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                      placeholder="Ex: Empresa ABC"
                    />
                  </div>
                  <div>
                    <Label>Slug (código de acesso)</Label>
                    <Input
                      value={newCompany.slug}
                      onChange={(e) => setNewCompany({ ...newCompany, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                      placeholder="Ex: empresa-abc (deixe vazio para gerar automaticamente)"
                    />
                    <p className="text-xs text-slate-500 mt-1">Usado no login para identificar a empresa</p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                  <Button onClick={handleCreateCompany} disabled={creating}>
                    {creating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Criar
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Tema</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {companies.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-slate-500">
                      Nenhuma empresa cadastrada
                    </TableCell>
                  </TableRow>
                ) : (
                  companies.map((company) => (
                    <TableRow key={company.id}>
                      <TableCell className="font-medium">{company.name}</TableCell>
                      <TableCell>
                        <code className="px-2 py-1 bg-slate-100 rounded text-sm">{company.slug}</code>
                      </TableCell>
                      <TableCell>
                        <div 
                          className="w-6 h-6 rounded-full border-2 border-white shadow"
                          style={{ backgroundColor: company.theme?.primary || '#0ea5e9' }}
                          title={`Cor: ${company.theme?.primary || '#0ea5e9'}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Badge variant={company.is_active ? 'default' : 'secondary'}>
                          {company.is_active ? 'Ativa' : 'Bloqueada'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewUsers(company)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Users className="w-4 h-4 mr-1" />
                            Usuários
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(company)}
                            title="Editar empresa"
                            className="text-slate-600 hover:text-slate-800"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleToggleStatus(company.id, company.is_active)}
                            title={company.is_active ? 'Bloquear' : 'Ativar'}
                          >
                            {company.is_active ? (
                              <ToggleRight className="w-5 h-5 text-green-600" />
                            ) : (
                              <ToggleLeft className="w-5 h-5 text-slate-400" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => confirmDelete(company)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Edit Company Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-blue-500" />
              Editar Empresa
            </DialogTitle>
          </DialogHeader>
          
          {editingCompany && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Nome da Empresa</Label>
                <Input
                  value={editingCompany.name}
                  onChange={(e) => setEditingCompany({ ...editingCompany, name: e.target.value })}
                />
              </div>
              
              <div>
                <Label>Slug</Label>
                <Input
                  value={editingCompany.slug}
                  onChange={(e) => setEditingCompany({ ...editingCompany, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                />
                <p className="text-xs text-slate-500 mt-1">Cuidado: mudar o slug afeta o login dos usuários</p>
              </div>
              
              <div>
                <Label className="flex items-center gap-2">
                  <Palette className="w-4 h-4" />
                  Tema / Cor Principal
                </Label>
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {themes.map((theme) => (
                    <button
                      key={theme.primary}
                      onClick={() => setEditingCompany({ 
                        ...editingCompany, 
                        theme: { primary: theme.primary, primary_dark: theme.primary_dark }
                      })}
                      className={`p-2 rounded-lg border-2 transition-all ${
                        editingCompany.theme?.primary === theme.primary 
                          ? 'border-slate-900 scale-105' 
                          : 'border-transparent hover:border-slate-300'
                      }`}
                      title={theme.name}
                    >
                      <div 
                        className="w-full h-8 rounded"
                        style={{ backgroundColor: theme.primary }}
                      />
                      <p className="text-[10px] text-center mt-1 text-slate-600">{theme.name}</p>
                    </button>
                  ))}
                </div>
                
                <div className="mt-3">
                  <Label className="text-xs">Ou escolha uma cor personalizada:</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="color"
                      value={editingCompany.theme?.primary || '#0ea5e9'}
                      onChange={(e) => setEditingCompany({
                        ...editingCompany,
                        theme: { primary: e.target.value, primary_dark: e.target.value }
                      })}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={editingCompany.theme?.primary || '#0ea5e9'}
                      onChange={(e) => setEditingCompany({
                        ...editingCompany,
                        theme: { primary: e.target.value, primary_dark: e.target.value }
                      })}
                      className="flex-1"
                      placeholder="#0ea5e9"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancelar</Button>
            <Button onClick={handleSaveEdit} disabled={savingEdit}>
              {savingEdit && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              <Save className="w-4 h-4 mr-2" />
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Empresa</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir <strong>{companyToDelete?.name}</strong>?
              <br /><br />
              <span className="text-red-600 font-medium">
                ⚠️ Esta ação é irreversível! Todos os dados serão perdidos.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCompany}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleting}
            >
              {deleting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete User Confirm Dialog */}
      <AlertDialog open={showDeleteUserConfirm} onOpenChange={setShowDeleteUserConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Usuário</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o usuário <strong>{userToDelete?.name}</strong> ({userToDelete?.email})?
              <br /><br />
              <span className="text-red-600 font-medium">
                Esta ação é irreversível!
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              className="bg-red-600 hover:bg-red-700"
              disabled={deletingUser}
            >
              {deletingUser && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Users Dialog */}
      <Dialog open={showUsersDialog} onOpenChange={setShowUsersDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-500" />
              Usuários - {selectedCompany?.name}
            </DialogTitle>
            <DialogDescription>
              Slug: <code className="px-2 py-1 bg-slate-100 rounded">{selectedCompany?.slug}</code>
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {loadingUsers ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : companyUsers.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>Nenhum usuário cadastrado</p>
              </div>
            ) : (
              <ScrollArea className="h-[300px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {companyUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.name || '-'}</TableCell>
                        <TableCell className="text-sm">{user.email}</TableCell>
                        <TableCell>
                          <Badge className={getRoleColor(user.role)}>
                            {getRoleLabel(user.role)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openResetDialog(user)}
                              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                            >
                              <Key className="w-4 h-4 mr-1" />
                              Resetar
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeleteUserDialog(user)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            )}
          </div>
          
          <DialogFooter className="flex justify-between">
            <Button variant="outline" onClick={() => setShowCreateUserDialog(true)}>
              <UserPlus className="w-4 h-4 mr-2" />
              Novo Usuário
            </Button>
            <Button variant="outline" onClick={() => setShowUsersDialog(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-500" />
              Resetar Senha
            </DialogTitle>
            <DialogDescription>
              Usuário: <strong>{selectedUser?.name || selectedUser?.email}</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div>
              <Label>Nova Senha</Label>
              <Input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Digite a nova senha"
              />
              <p className="text-xs text-slate-500 mt-1">Mínimo 6 caracteres</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetDialog(false)}>Cancelar</Button>
            <Button onClick={handleResetPassword} disabled={resettingPassword}>
              {resettingPassword && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Resetar Senha
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={showCreateUserDialog} onOpenChange={setShowCreateUserDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-green-500" />
              Novo Usuário - {selectedCompany?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div>
              <Label>Nome</Label>
              <Input
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                placeholder="Nome completo"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="email@exemplo.com"
              />
            </div>
            <div>
              <Label>Senha</Label>
              <Input
                type="text"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="Senha inicial"
              />
            </div>
            <div>
              <Label>Perfil</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="diretoria">Diretoria</SelectItem>
                  <SelectItem value="user">Usuário</SelectItem>
                  <SelectItem value="viewer">Visualizador</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateUserDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreateUser} disabled={creatingUser}>
              {creatingUser && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Criar Usuário
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Super Admin Credentials Dialog */}
      <Dialog open={showCredentialsDialog} onOpenChange={setShowCredentialsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-blue-500" />
              Minhas Credenciais
            </DialogTitle>
            <DialogDescription>
              Atualize seu email e/ou senha de Super Admin
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div>
              <Label className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Novo Email (opcional)
              </Label>
              <Input
                type="email"
                value={credentials.email}
                onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                placeholder="Deixe vazio para manter o atual"
              />
            </div>
            
            <div>
              <Label className="flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Nova Senha (opcional)
              </Label>
              <Input
                type="password"
                value={credentials.newPassword}
                onChange={(e) => setCredentials({ ...credentials, newPassword: e.target.value })}
                placeholder="Deixe vazio para manter a atual"
              />
              <p className="text-xs text-slate-500 mt-1">Mínimo 6 caracteres</p>
            </div>
            
            <div className="pt-4 border-t">
              <Label className="flex items-center gap-2 text-amber-600">
                <Key className="w-4 h-4" />
                Senha Atual (obrigatório)
              </Label>
              <Input
                type="password"
                value={credentials.currentPassword}
                onChange={(e) => setCredentials({ ...credentials, currentPassword: e.target.value })}
                placeholder="Digite sua senha atual"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCredentialsDialog(false)}>Cancelar</Button>
            <Button onClick={handleUpdateCredentials} disabled={savingCredentials}>
              {savingCredentials && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              <Save className="w-4 h-4 mr-2" />
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminDashboard;
